/**
 * Wire shapes for structured messages and diagnostics.
 *
 * Mirrors `com.wm.service.message` and `com.wm.service.diagnostic` on the server, and the AutoBean
 * interfaces in `com.ots.database.{message,diagnostic}` that GWT decodes them into.
 *
 * Every enum crosses the wire as its string code and is typed here as a union with a `string`
 * fallback. That is deliberate: a server that learns a new severity or value type must not blank out
 * a message in an older client, so unknown codes have to fall through to a sane default rather than
 * fail a lookup.
 */

export type DiagnosticSeverity = 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';

/** What kind of value a token holds. Drives presentation mechanics, not valence. */
export type ValueType =
  | 'TEXT' | 'NUMBER' | 'QUANTITY' | 'MONEY' | 'PERCENT'
  | 'DATE' | 'TIME' | 'DATETIME' | 'DURATION'
  | 'ID' | 'ENTITY_REF' | 'SITE' | 'PERSON' | 'ASSET'
  | 'CODE' | 'URL' | 'BOOLEAN' | 'LIST' | 'STATUS';

/** What a token means in its sentence. The primary input to styling. */
export type TokenRole =
  | 'SUBJECT' | 'EXPECTED' | 'ACTUAL' | 'DELTA' | 'THRESHOLD'
  | 'LOCATION' | 'SOURCE' | 'COUNT' | 'REMEDY' | 'CONTEXT';

/** Colour valence, independent of meaning. Overrides the role's default when set. */
export type Tone = 'NEUTRAL' | 'POSITIVE' | 'CAUTION' | 'NEGATIVE' | 'MUTED';

export type Emphasis =
  | 'BOLD' | 'ITALIC' | 'UNDERLINE' | 'STRIKETHROUGH'
  | 'MONOSPACE' | 'HIGHLIGHT' | 'SUBTLE';

export type ObjectRefType =
  | 'TTA' | 'TRANSPORT' | 'SHIFT' | 'SERVICE_WORK' | 'DISPATCH'
  | 'SITE' | 'CONTAINER' | 'PERSON' | 'COMPANY'
  | 'RATE' | 'RATE_MODEL' | 'INVOICE' | 'TICKET_CODE' | 'WELL_STAGE'
  | 'REGION' | 'REPORT' | 'ACTIVITY_PERIOD' | 'NONE';

/** A pointer from a message back to the thing it is about. */
export interface ObjectRef {
  objectType?: ObjectRefType | string;
  objectId?: string;
  /** Catalog the id belongs to. Ids repeat across catalogs, so an id alone is ambiguous. */
  database?: string;
  regionId?: string;
  /** User-facing WaterGo ids. */
  resourceIdentifiers?: string[];
  /** Path to the field at fault, e.g. ["pickupTransport","quantityDecimal"]. */
  fieldPath?: string[];
  occurredDttm?: number;
  /** Extra identity for re-querying: personId, shiftId, pickupTransportId, deliveryTransportId. */
  context?: Record<string, string>;
  label?: string;
}

/** One interpolated value inside a message. */
export interface MessageToken {
  key?: string;
  /** Server-rendered text. Rendered as a text node, never as markup. */
  display?: string;
  /** Machine-readable form, for a client that wants to re-render in the viewer's locale. */
  rawValue?: string;
  type?: ValueType | string;
  role?: TokenRole | string;
  tone?: Tone | string;
  emphasis?: Array<Emphasis | string>;
  unit?: string;
  hint?: string;
  /** When present, this token is a link to that object. */
  link?: ObjectRef;
}

/** One label/value evidence row shown beneath a message. */
export interface MessageFact {
  label?: string;
  value?: MessageToken;
}

export interface RichMessage {
  /** Template with `{key}` placeholders. */
  summary?: string;
  /** Longer template, shown when expanded. */
  detail?: string;
  tokens?: MessageToken[];
  facts?: MessageFact[];
  /** Fully rendered fallback. */
  plainText?: string;
}

export interface DiagnosticRemediation {
  message?: RichMessage;
  actionKey?: string;
  actionParams?: Record<string, string>;
}

export interface DiagnosticCore {
  id?: string;
  /** Namespaced and stable -- "report.site.missing". */
  code?: string;
  severity?: DiagnosticSeverity | string;
  blocking?: boolean;
  category?: string;
  title?: string;
  message?: RichMessage;
  targets?: ObjectRef[];
  remediation?: DiagnosticRemediation;
  dedupeKey?: string;
  /** How many identical occurrences folded into this one. */
  occurrenceCount?: number;
  createDttm?: number;
}

export interface Diagnostic {
  /** Discriminator: REPORT, COST, ... */
  module?: string;
  core?: DiagnosticCore;
  /** Report-specific; absent on other modules. */
  phase?: string;
  reportType?: string;
  sessionId?: string;
}

export interface DiagnosticRun {
  sessionId?: string;
  module?: string;
  subject?: string;
  startedDttm?: number;
  completedDttm?: number;
  counts?: Partial<Record<DiagnosticSeverity, number>> & Record<string, number>;
  worstSeverity?: DiagnosticSeverity | string;
  blocking?: boolean;
  /** How many diagnostics were dropped at the collector's cap. */
  suppressedCount?: number;
  diagnostics?: Diagnostic[];
}

export const SEVERITY_ORDER: DiagnosticSeverity[] = ['CRITICAL', 'ERROR', 'WARNING', 'INFO'];

/** Higher is worse. Unknown severities rank as WARNING so a new code never sorts to the top. */
export const severityRank = (severity?: string): number => {
  switch (severity) {
    case 'CRITICAL':
      return 3;
    case 'ERROR':
      return 2;
    case 'INFO':
      return 0;
    default:
      return 1;
  }
};
